/**
 * Authentication OAuth Error Handler
 *
 * Handles OAuth authentication errors specifically for user sign-in flows
 * (Google/Microsoft OAuth for registration/login), distinct from connection OAuth flows.
 */

import { authLogger } from './loggerInstance';
import {
  DEFAULT_USER_FACING_ERROR_MESSAGE,
  toUserFacingErrorMessage,
} from './userFacingErrorMessage';

function readFirebaseAuthErrorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as { code?: unknown; message?: unknown };
  if (typeof e.code === 'string' && e.code.startsWith('auth/')) {
    return e.code;
  }
  const msg = typeof e.message === 'string' ? e.message : '';
  const match = /\b(auth\/[a-z0-9-]+)\b/i.exec(msg);
  return match ? match[1].toLowerCase() : null;
}

/** User-facing copy for Firebase Auth OAuth popup flows (never surface raw `auth/...` codes). */
const FIREBASE_OAUTH_SIGN_IN_MESSAGES: Record<string, string> = {
  'auth/popup-closed-by-user': 'Sign-in was cancelled. Try again when you are ready.',
  'auth/cancelled-popup-request':
    'Another sign-in is already in progress. Please finish or cancel it and try again.',
  'auth/popup-blocked':
    'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.',
  'auth/network-request-failed':
    'Network error while signing in. Check your connection and try again.',
  'auth/too-many-requests': 'Too many sign-in attempts. Please wait a moment and try again.',
  'auth/user-disabled': 'This account has been disabled. Contact support if you need help.',
  'auth/operation-not-allowed': 'This sign-in method is not available. Please contact support.',
  'auth/account-exists-with-different-credential':
    'An account already exists with this email using a different sign-in method.',
  'auth/internal-error': DEFAULT_USER_FACING_ERROR_MESSAGE,
  'auth/invalid-credential': 'Sign-in could not be completed. Please try again.',
};

/**
 * Builds a safe message when Google/Microsoft OAuth sign-in fails before the unified API call.
 * Firebase errors often use `auth/code` strings; those must not reach toasts verbatim.
 */
export function buildOAuthSignInFailureMessage(
  provider: 'google' | 'microsoft',
  providerError: unknown,
): string {
  const label = provider === 'google' ? 'Google' : 'Microsoft';
  const code = readFirebaseAuthErrorCode(providerError);

  if (code && FIREBASE_OAUTH_SIGN_IN_MESSAGES[code]) {
    return FIREBASE_OAUTH_SIGN_IN_MESSAGES[code];
  }
  if (code) {
    return `${label} sign-in could not be completed. Please try again.`;
  }

  const raw =
    providerError instanceof Error
      ? providerError.message
      : typeof providerError === 'string'
        ? providerError
        : '';
  const withoutCodes = raw
    .replace(/\bauth\/[a-z0-9-]+\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  const sanitized = toUserFacingErrorMessage(
    withoutCodes || `${label} sign-in could not be completed.`,
    DEFAULT_USER_FACING_ERROR_MESSAGE,
  );

  if (sanitized === DEFAULT_USER_FACING_ERROR_MESSAGE && !withoutCodes) {
    return `${label} sign-in could not be completed. Please try again.`;
  }
  if (withoutCodes.length > 0 && sanitized !== DEFAULT_USER_FACING_ERROR_MESSAGE) {
    return `${label} sign-in could not be completed. ${sanitized}`;
  }
  return `${label} sign-in could not be completed. Please try again.`;
}

/**
 * Interface for OAuth verification error handling result
 */
export interface OAuthVerificationErrorResult {
  isVerificationError: boolean;
  shouldShowDialog: boolean;
  errorMessage?: string;
}

/**
 * Check if an OAuth error is a verification code requirement error
 * and handle the verification dialog display logic
 *
 * This is shared between CreateAccount.tsx and Login.tsx to avoid duplication
 *
 * @param error - The caught error from OAuth sign-in attempt
 * @param provider - The OAuth provider ('google' | 'microsoft')
 * @param providerName - Human-readable provider name (e.g., 'Google', 'Microsoft')
 * @param showInfo - Toast notification function to show verification message
 * @param setVerificationProvider - Function to set the verification provider
 * @param setShowVerificationDialog - Function to show/hide verification dialog
 * @param setLoading - Function to set loading state (optional, for Login.tsx)
 * @returns OAuthVerificationErrorResult indicating if this was a verification error
 */
export const handleOAuthVerificationError = (
  error: Error | unknown,
  provider: 'google' | 'microsoft',
  providerName: string,
  showInfo: (message: string) => void,
  setVerificationProvider: (provider: 'google' | 'microsoft') => void,
  setShowVerificationDialog: (show: boolean) => void,
  setLoading?: (loading: boolean) => void,
): OAuthVerificationErrorResult => {
  // Extract the error detail to check for verification code requirements
  let originalDetail = '';

  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as any;
    originalDetail = axiosError.response?.data?.detail || '';
  }

  // Check if this is a verification code requirement error
  const isVerificationError =
    originalDetail.includes('Verification code is required') &&
    originalDetail.includes('new account registration');

  if (isVerificationError) {
    authLogger.info(`Showing verification code dialog for ${providerName} sign-in registration`, {
      module: 'AuthOAuthErrorHandler',
      provider,
    });

    // Show info toast notification
    showInfo('Registering new user - verification code required');

    // Set up verification dialog
    setVerificationProvider(provider);
    setShowVerificationDialog(true);

    // Clear loading state if setter provided (used in Login.tsx)
    if (setLoading) {
      setLoading(false);
    }

    return {
      isVerificationError: true,
      shouldShowDialog: true,
    };
  }

  return {
    isVerificationError: false,
    shouldShowDialog: false,
  };
};
